import React, { useState } from 'react'

import { FileTextIcon, PlayIcon } from 'lucide-react'

import { Box, Stack, Typography, useTheme } from '@mui/material'

import { Image } from '@repo/ui'

import { ImageGalleryModal } from 'components/feedback/modals/ImageGalleryModal'

/**
 * A message's or request's attachments, as thumbnails that open full size.
 *
 * ## Why a component rather than the inline link it replaced
 *
 * The thread rendered `{x.attachmentUrl && <a>View attachment</a>}` TWICE — once for the
 * request's opening message and once per reply — which is the second hand-roll working-
 * conventions §36 says to stop at. With a list instead of a single URL each copy would have
 * grown its own map, its own image-vs-PDF branch and its own viewer wiring, and they would
 * have drifted. One component, two call sites.
 *
 * ## Why thumbnails instead of a text link
 *
 * A support attachment is a screenshot in the overwhelming majority of cases, and "View
 * attachment" tells a staffer nothing about which screenshot it is — with several attached it
 * tells them nothing at all, N identical links in a row. A thumbnail grid is scannable, and
 * clicking one opens the shared lightbox at that image, so triage does not need a new browser
 * tab per file.
 *
 * PDFs cannot be thumbnailed by an `<img>`, so they keep a labelled tile that opens in a new
 * tab — the honest affordance, rather than a broken image icon.
 */

/** Whether a URL is something an `<img>` can render. */
const isImageUrl = (url: string): boolean =>
  /\.(jpe?g|png|gif|webp|avif|bmp)(\?|#|$)/i.test(url)

/**
 * Whether a URL is a video.
 *
 * Gets a play glyph rather than the document one: a screen recording is the single most useful
 * thing a reporter can attach, and a staffer scanning a thread needs to see at a glance that
 * there is something to WATCH. Opening it hands the file to the browser, which plays mp4/webm
 * natively — inline playback inside the gallery would be better still and is a follow-up, not a
 * reason to ship it looking like a PDF.
 */
const isVideoUrl = (url: string): boolean =>
  /\.(mp4|m4v|mov|webm)(\?|#|$)/i.test(url)

/** Last path segment, for the PDF tile's label. */
const fileNameOf = (url: string): string => {
  try {
    const path = new URL(url, window.location.origin).pathname
    return decodeURIComponent(path.slice(path.lastIndexOf('/') + 1)) || 'Attachment'
  } catch {
    return 'Attachment'
  }
}

const TILE = 72

export const AttachmentList = ({ urls }: { urls: string[] }) => {
  const theme = useTheme()
  const [galleryIndex, setGalleryIndex] = useState<number | null>(null)

  if (!urls || urls.length === 0) return null

  // Only the renderable ones go to the lightbox, and the map carries a tile's position in the
  // FILTERED array — so clicking the second image in [pdf, image, image] does not open the third.
  const imageUrls = urls.filter(isImageUrl)
  const galleryIndexOf = (url: string) => imageUrls.indexOf(url)

  return (
    <>
      <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 2 }}>
        {urls.map(url => {
          const isImage = isImageUrl(url)

          if (!isImage) {
            return (
              <Stack
                key={url}
                component="a"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                alignItems="center"
                justifyContent="center"
                spacing={1}
                sx={{
                  width: TILE,
                  height: TILE,
                  flexShrink: 0,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.grey[300]}`,
                  backgroundColor: theme.palette.grey[50],
                  color: 'text.secondary',
                  textDecoration: 'none',
                  p: 1,
                  '&:hover': { borderColor: theme.palette.brand[600] },
                }}
              >
                {isVideoUrl(url) ? (
                  <PlayIcon size={20} strokeWidth={1} />
                ) : (
                  <FileTextIcon size={20} strokeWidth={1} />
                )}
                <Typography variant="body4" noWrap sx={{ maxWidth: TILE - 12 }}>
                  {fileNameOf(url)}
                </Typography>
              </Stack>
            )
          }

          return (
            <Box
              key={url}
              role="button"
              tabIndex={0}
              aria-label="View attachment full size"
              onClick={() => setGalleryIndex(galleryIndexOf(url))}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  setGalleryIndex(galleryIndexOf(url))
                }
              }}
              sx={{
                width: TILE,
                height: TILE,
                flexShrink: 0,
                borderRadius: 2,
                overflow: 'hidden',
                border: `1px solid ${theme.palette.grey[300]}`,
                cursor: 'zoom-in',
                '&:hover': { borderColor: theme.palette.brand[600] },
                '&:focus-visible': {
                  outline: `2px solid ${theme.palette.brand[600]}`,
                  outlineOffset: 1,
                },
              }}
            >
              <Image src={url} fit="cover" sx={{ width: TILE, height: TILE }} />
            </Box>
          )
        })}
      </Stack>

      <ImageGalleryModal
        open={galleryIndex !== null}
        onClose={() => setGalleryIndex(null)}
        images={imageUrls}
        initialIndex={galleryIndex ?? 0}
        title="Attachments"
      />
    </>
  )
}
